<script setup lang="ts">
import { ref } from 'vue'

import { HeadingType } from '@xcel-next/core/enums'

import Icon from '../shared/Icon.vue'

import type { FaqParagraph } from '../../utils/paragraph-schema/paragraphs'

type FaqItems = NonNullable<FaqParagraph['faq']['items']>
type FaqItem = FaqItems[number]

withDefaults(defineProps<{
  items: FaqParagraph['faq']['items'],
  isPreview?: boolean
}>(), {
  isPreview: false
})

const previewAnswerState = ref<Record<number, boolean>>({})
const faqRefs = ref<Record<string, HTMLElement>>({})

function hasInitialAnswer (id: number, answer: FaqItem['answer'] = undefined): boolean {
  if (!(id in previewAnswerState.value)) {
    previewAnswerState.value[id] = !!answer
  }
  return previewAnswerState.value[id]
}

function toggleFaq (refKey: string, question: string = '') {
  const el = faqRefs.value[refKey]
  if (!el) return
  const collapsed = el.classList.toggle('bx-js-faq-hide')
  const div = el.querySelector<HTMLElement>('div')
  if (!div) return
  div.setAttribute('aria-label', `Antwort zu folgender Frage ${collapsed ? 'öffnen' : 'schliessen'}: ${question}`)
  div.setAttribute('aria-expanded', String(!collapsed))
}

function headingTag (item: FaqItem): string {
  return item.heading && item.heading !== HeadingType.none ? item.heading : 'div'
}
</script>

<template v-if="items?.length">
  <div class="bx-content bx-content__continuous-text bx-typo--content-continuous-text">
    <section v-for="(item, fid) in items"
             :key="`faqQuestion-${fid}`"
             :ref="(el) => { if (el) faqRefs[`faqQuestion${fid}`] = el as HTMLElement }"
             :class="['bx-faq', 'bx-typo--faq', {'bx-js-faq-hide': !isPreview || (isPreview && hasInitialAnswer(fid, item.answer)) }]">
      <div class="bx-faq__question"
           tabindex="0"
           role="button"
           :aria-label="`Antwort zu folgender Frage öffnen: ${item.question}`"
           :aria-controls="`bx-aria-faq-${fid}`"
           aria-expanded="false"
           @click="toggleFaq(`faqQuestion${fid}`, item.question)"
           @keyup.enter="toggleFaq(`faqQuestion${fid}`, item.question)">
        <component :is="headingTag(item)">
          {{ item.question }}
        </component>
        <Icon name="arrow_toggle" />
      </div>
      <div :id="`bx-aria-faq-${fid}`"
           class="bx-faq__answer"
           v-html="item.answer?.html" />
    </section>
  </div>
</template>
